import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { usePollingFallback } from '@/hooks/usePollingFallback';

/** A job whose technician pressed "On my way" and has not started it yet. */
export interface EnRouteJob {
  id: string;
  job_number: string | null;
  status: string;
  customer_name: string | null;
  customer_suburb: string | null;
  assigned_tech_id: string | null;
  en_route_at: string | null;
  en_route_eta_minutes: number | null;
  en_route_eta_at: string | null;
  arrived_at: string | null;
  work_finished_at: string | null;
  techs: { name: string } | null;
}

// A trip older than this is a forgotten "On my way", not a technician on the road.
const MAX_TRIP_MS = 12 * 60 * 60_000;

/**
 * Technicians on the road right now, earliest departure first. Keyed under ['jobs'] so the
 * realtime jobs channel refreshes it the moment the app sends "On my way" or "Start".
 */
export function useEnRouteJobs() {
  const refetchInterval = usePollingFallback(60_000);

  return useQuery({
    queryKey: ['jobs', 'en-route'],
    refetchInterval,
    queryFn: async (): Promise<EnRouteJob[]> => {
      const { data, error } = await supabase
        .from('jobs')
        .select(
          'id, job_number, status, customer_name, customer_suburb, assigned_tech_id, en_route_at, en_route_eta_minutes, en_route_eta_at, arrived_at, work_finished_at, techs(name)',
        )
        .not('en_route_at', 'is', null)
        .is('arrived_at', null)
        .in('status', ['scheduled', 'in_progress', 'quote_sent'])
        .gte('en_route_at', new Date(Date.now() - MAX_TRIP_MS).toISOString())
        .order('en_route_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as EnRouteJob[];
    },
  });
}
